/**
 * Iter XX.177 — Native Token Storage Hardening.
 * ------------------------------------------------------------------
 * The SINGLE frontend entry point for reading, writing, and clearing
 * the Gully bearer JWT (`gully_token`).
 *
 * Design (from the XX.176 → XX.177 audit):
 *
 *   Native (iOS / Android):
 *     - The token lives ONLY in the OS secure store:
 *         · iOS     → Keychain Services (via @aparajita/capacitor-secure-storage;
 *                     `whenUnlocked` access class, prefix `gully_`).
 *         · Android → AES-GCM encrypted, master key held in Android Keystore,
 *                     stored in SharedPreferences by the same plugin.
 *     - `window.localStorage` NEVER contains the token on native. Any legacy
 *       leftover from an XX.176 install is scrubbed at boot.
 *     - Synchronous reads (axios interceptor, gate checks) are served from
 *       an in-memory cache populated by `initTokenStorage()` at app boot.
 *
 *   Web (unchanged):
 *     - The token lives in `window.localStorage` under key `gully_token`,
 *       exactly as it has since day one.
 *     - Sync reads still hit `localStorage` directly to guarantee identical
 *       byte-for-byte behavior. The in-memory cache is not used on web.
 *
 * Public surface:
 *   initTokenStorage()      -> Promise<void>   (call once at app boot)
 *   getToken()              -> string | null   (sync, safe everywhere)
 *   hasToken()              -> boolean         (sync convenience)
 *   setToken(t)             -> Promise<void>   (sync cache update + async persist)
 *   clearToken()            -> Promise<void>   (sync cache clear + async persist)
 *   TOKEN_KEY               -> const           (external test hook only)
 *
 * Nothing else in the codebase touches `localStorage.getItem("gully_token")`
 * directly — see XX.177 audit for the list of migrated call sites.
 */
import { isNative, isIOS } from './nativePlatform';

export const TOKEN_KEY = 'gully_token';

// Iter XX.211 — iOS reinstall-parity sentinel.
// On Android, uninstalling the app wipes SharedPreferences (JWT store)
// AND WebView localStorage together, so reinstall is truly fresh: no
// JWT → anonymous session → server returns tos_accepted=false → first-
// install consent flow fires exactly once.
// On iOS, WKWebView localStorage IS wiped on uninstall but Keychain
// items are PERSISTED across app uninstall by default. That mismatch
// caused signed-in users to silently skip the first-install consent
// flow on reinstall (stale JWT hydrated → server matched an old TOS
// row for the persisted user_id → TOS bar never rendered).
// Fix: detect fresh install on iOS via a localStorage sentinel (which
// IS wiped on uninstall). If the sentinel is absent AND the Keychain
// still has a token, wipe the Keychain token BEFORE hydration. The
// resulting boot is byte-identical to a fresh Android install:
// anonymous, no cached identity, consent bars fire once.
const FRESH_INSTALL_SENTINEL_KEY = 'gully_first_install_v1';

// In-memory cache — the authoritative synchronous source of truth on native.
// On web this stays undefined; web reads pass through to localStorage
// unchanged so behavior is byte-for-byte identical to pre-XX.177.
let _tokenCache = null;
let _initialized = false;

// Lazy-load the plugin. Using `require` (not `await import()`) keeps the
// same module instance across production, dev, and Jest without ESM/CJS
// interop drift. The plugin is small (<5KB gzipped) so there's no
// bundle-size benefit to dynamic import here.
let _secureStorage = null;
function _getSecureStorage() {
  if (_secureStorage) return _secureStorage;
  try {
    // eslint-disable-next-line global-require
    const mod = require('@aparajita/capacitor-secure-storage');
    _secureStorage = mod.SecureStorage || (mod.default && mod.default.SecureStorage) || null;
  } catch (_e) {
    _secureStorage = null;
  }
  return _secureStorage;
}

/**
 * Iter XX.211 — iOS reinstall-parity guard.
 *
 * Detects a fresh install on iOS via a localStorage sentinel (which
 * WKWebView wipes on uninstall) and, if detected, forcibly removes any
 * stale Keychain-persisted token BEFORE hydration. This mirrors the
 * Android uninstall-wipe contract exactly:
 *
 *   Android uninstall → SharedPreferences + localStorage BOTH wiped
 *   iOS    uninstall → localStorage wiped, Keychain NOT wiped by default
 *
 * After the sentinel is set on first boot post-install, subsequent
 * cold-starts see it and skip the wipe path (idempotent).
 *
 * On Android and web this is a NO-OP — Android already wipes on
 * uninstall, web has no Keychain to reconcile.
 *
 * Returns `true` when a wipe was performed, `false` otherwise. The
 * return value is used only by tests.
 */
async function _ensureFreshInstallSentinel(store) {
  if (!isIOS()) return false;

  let sentinel = null;
  try {
    sentinel = window.localStorage.getItem(FRESH_INSTALL_SENTINEL_KEY);
  } catch (_e) {
    // localStorage unreachable — best-effort: don't touch Keychain.
    return false;
  }

  if (sentinel && sentinel.length > 0) {
    // Not a fresh install — Keychain state is authoritative for this
    // install-lifetime; leave it alone.
    return false;
  }

  // Fresh install detected. Wipe any stale Keychain token so hydration
  // sees an anonymous device — same starting state Android reinstall
  // gives us for free.
  let wiped = false;
  if (store) {
    try {
      await store.removeItem(TOKEN_KEY);
      wiped = true;
    } catch (_e) {
      // Never crash the app for this — worst case, the caller boots
      // with the stale token and the consent bar is skipped again;
      // sentinel is deliberately NOT set so we retry on next boot.
      return false;
    }
  }

  // Only mark the sentinel AFTER we've successfully reconciled the
  // Keychain. If the wipe failed we deliberately leave the sentinel
  // absent so the next boot retries.
  try {
    window.localStorage.setItem(
      FRESH_INSTALL_SENTINEL_KEY,
      new Date().toISOString(),
    );
  } catch (_e) {
    /* noop */
  }

  return wiped;
}

/**
 * Called once at app boot from index.js — BEFORE React renders, so the
 * very first paint has the correct auth state. Fast:
 *   - Web: no-op returning immediately (reads happen straight from
 *          localStorage synchronously later).
 *   - Native: single Keychain / EncryptedSharedPreferences read into
 *          the in-memory cache. Also opportunistically scrubs any
 *          plaintext `gully_token` left behind by an XX.176 install.
 *
 * Iter XX.211 — On iOS, a fresh-install sentinel check runs BEFORE
 * hydration to enforce the Android uninstall-wipe contract. See
 * `_ensureFreshInstallSentinel` above.
 */
export async function initTokenStorage() {
  if (_initialized) return;
  _initialized = true;

  if (!isNative()) {
    return;
  }

  const store = _getSecureStorage();
  if (!store) {
    // Plugin didn't resolve — unusual on native. Fail closed: no token.
    _tokenCache = null;
    return;
  }

  // Iter XX.211 — reconcile stale iOS Keychain against uninstall BEFORE
  // hydration. No-op on Android.
  await _ensureFreshInstallSentinel(store);

  try {
    const v = await store.getItem(TOKEN_KEY);
    _tokenCache = typeof v === 'string' && v.length > 0 ? v : null;
  } catch (_e) {
    _tokenCache = null;
  }

  // Scrub any lingering plaintext token from a prior XX.176 build. This
  // is the one and only time we touch `localStorage.gully_token` on
  // native — after this call it stays gone.
  try {
    if (window && window.localStorage) {
      window.localStorage.removeItem(TOKEN_KEY);
    }
  } catch (_e) {
    /* localStorage unavailable in native shell — nothing to scrub */
  }
}

/**
 * Synchronous read.
 *   - Native: returns the in-memory cache populated at boot / updated by
 *             setToken/clearToken. Never inspects localStorage.
 *   - Web:    reads localStorage directly — byte-for-byte identical to
 *             the pre-XX.177 inline `localStorage.getItem("gully_token")`.
 */
export function getToken() {
  if (isNative()) {
    return _tokenCache;
  }
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch (_e) {
    return null;
  }
}

/**
 * Boolean convenience. Same semantics as `Boolean(getToken())`.
 */
export function hasToken() {
  return Boolean(getToken());
}

/**
 * Persist a token.
 *   - Native: update in-memory cache immediately (so the next
 *             synchronous read sees it), then async-write to the OS
 *             secure store. localStorage is never touched.
 *   - Web:    write to localStorage — exactly as before.
 */
export async function setToken(token) {
  if (typeof token !== 'string' || token.length === 0) {
    // Defensive: never persist empty tokens. Callers already guard, but
    // this prevents a rogue caller from wiping the store via a blank.
    return;
  }

  if (isNative()) {
    _tokenCache = token;
    const store = _getSecureStorage();
    if (!store) return;
    try {
      await store.setItem(TOKEN_KEY, token);
    } catch (_e) {
      /* Fail closed — the cache still holds the token for this session,
         next boot will re-authenticate. Do NOT fall back to localStorage. */
    }
    return;
  }

  try {
    window.localStorage.setItem(TOKEN_KEY, token);
  } catch (_e) {
    /* noop — localStorage may be disabled in privacy mode */
  }
}

/**
 * Clear the token (logout, purchase abort, identity revoke).
 *   - Native: clear the cache immediately, then async-remove from the
 *             OS secure store.
 *   - Web:    localStorage.removeItem — exactly as before.
 */
export async function clearToken() {
  if (isNative()) {
    _tokenCache = null;
    const store = _getSecureStorage();
    if (!store) return;
    try {
      await store.removeItem(TOKEN_KEY);
    } catch (_e) {
      /* noop */
    }
    return;
  }

  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch (_e) {
    /* noop */
  }
}

// ---- Test-only helpers ------------------------------------------------
// Exported so unit tests can reset module-level state between cases.
// Not documented as part of the public surface. Do not call from app code.
export function __resetForTests() {
  _tokenCache = null;
  _initialized = false;
  _secureStorage = null;
}

// Iter XX.211 — test-only accessor for the fresh-install sentinel key so
// pytest / vitest can wipe it between install-lifecycle assertions. Not
// part of the runtime public surface.
export const _FRESH_INSTALL_SENTINEL_KEY = FRESH_INSTALL_SENTINEL_KEY;
export { _ensureFreshInstallSentinel };
